import React, { useState, useEffect } from 'react';

function AIAnalyzeModal({ isOpen, onClose, teamData }) {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [report, setReport] = useState(null);
  
  // ✅ 추가: 로그인한 사용자의 이름을 관리할 상태
  const [userName, setUserName] = useState('사용자');

  // 기여도 점수에 따른 칭호와 스타일을 결정하는 함수
  const getGradeInfo = (score) => {
    if (score >= 90) return { tag: "👑 프로젝트의 신", color: "#6200EA", bgColor: "#F3E5F5" };
    if (score >= 70) return { tag: "🔥 핵심 엔진", color: "#D32F2F", bgColor: "#FFEBEE" };
    if (score >= 50) return { tag: "✅ 성실 협력자", color: "#2E7D32", bgColor: "#E8F5E9" };
    if (score >= 30) return { tag: "🌱 성장 중인 새싹", color: "#F57C00", bgColor: "#FFF3E0" };
    if (score > 0) return { tag: "⚡ 분위기 메이커", color: "#455A64", bgColor: "#ECEFF1" };
    return { tag: "💤 성장 동력 대기 중", color: "#9E9E9E", bgColor: "#F5F5F5" };
  };

  // 기여도와 과업 내용을 바탕으로 GPT 스타일 멘트를 생성하는 함수
  const generateComment = (member, currentUserName) => {
    const { name, task, contribution: score } = member;
    if (score >= 90) return `${name} 님은 '${task}' 뿐만 아니라 팀 전체의 방향성을 제시하며 압도적인 캐리력을 보여주셨습니다. 존재 자체가 팀의 자산입니다!`;
    if (score >= 70) return `${name} 님은 부여된 '${task}' 작업을 완벽하게 수행하며 팀의 속도를 최고조로 끌어올린 일등 공신입니다.`;
    if (score >= 50) return `${name} 님은 '${task}' 과업을 기한 내에 성실히 이행하며 팀 협업의 톱니바퀴가 원활히 돌아갈 수 있도록 든든하게 받쳐주었습니다.`;
    if (score >= 30) return `${name} 님은 현재 '${task}' 단계를 차근차근 밟아나가고 있습니다. 조금만 더 속도를 내면 팀에 엄청난 보탬이 될 잠재력이 보여요!`;
    if (score > 0) return `${name} 님은 현재 '${task}'를 준비하며 팀원들과 소통하고 있습니다. 본격적인 몰입을 통해 더 큰 시너지를 기대해 봅니다!`;
    
    // ✅ 핵심 수정: 하드코딩된 이름 대신 로그인한 실제 사용자 이름(currentUserName)을 사용하여 멘트 생성
    return `${name} 님, 아직 '${task}'에 대한 기록이 부족합니다. ${currentUserName} 님과 팀원들이 기다리고 있어요! 이제 실력을 보여줄 시간입니다!`;
  };

  useEffect(() => {
    if (isOpen && teamData) {
      setIsAnalyzing(true);

      // ✅ 핵심 수정: LoginPage에서 저장한 'currentUser' 정보를 실시간으로 읽어옴[cite: 8]
      const loggedInUser = JSON.parse(localStorage.getItem('currentUser'));
      const currentName = loggedInUser && loggedInUser.name ? loggedInUser.name : '사용자';
      setUserName(currentName);
      
      // 기여도 저장 API 연동 시뮬레이션
      setTimeout(() => {
        const analyzeResult = teamData.members.map(member => {
          const grade = getGradeInfo(member.contribution);
          return {
            name: member.name,
            score: member.contribution,
            feedback: generateComment(member, currentName), // 현재 로그인 유저 이름을 멘트 생성기에 전달
            tag: grade.tag,
            tagColor: grade.color,
            tagBg: grade.bgColor
          };
        });
        
        setReport(analyzeResult);
        setIsAnalyzing(false);
      }, 2500); 
    }
  }, [isOpen, teamData]);

  if (!isOpen) return null;

  return (
    <div style={overlayStyle}>
      <div style={modalCardStyle}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>✨ GPT 기여도 실시간 분석 리포트</h2>
          <button onClick={onClose} style={closeBtnStyle}>×</button>
        </div>

        {isAnalyzing ? (
          <div style={loadingAreaStyle}>
            <div style={spinnerStyle}></div>
            <p style={loadingTextStyle}>AI가 팀원들의 작업 로그와 기여도를 정밀 분석 중입니다...</p>
            <p style={subLoadingStyle}>기여도 저장 API로부터 최신 데이터를 동기화하고 있습니다.</p>
          </div>
        ) : (
          <div style={contentAreaStyle}>
            <div style={summaryBoxStyle}>
              <p>📊 <strong>종합 분석:</strong> 현재 <strong>{teamData.name}</strong>팀은 <strong>{teamData.status}</strong> 단계에서 활발히 협업 중입니다. AI가 산정한 데이터를 확인하세요.</p>
            </div>
            
            {report && report.map((item, idx) => (
              <div key={idx} style={reportCardStyle}>
                <div style={cardTopStyle}>
                  <span style={nameStyle}>{item.name} 님</span>
                  <span style={{
                    backgroundColor: item.tagBg, 
                    color: item.tagColor, 
                    padding: '6px 12px', 
                    borderRadius: '10px', 
                    fontSize: '11px', 
                    fontWeight: '1000',
                    border: `1px solid ${item.tagColor}44`
                  }}>{item.tag}</span>
                </div>
                <div style={gaugeBarStyle}>
                  <div style={{...gaugeProgressStyle, width: `${item.score}%`, backgroundColor: item.tagColor}}></div>
                </div>
                <p style={feedbackStyle}>"{item.feedback}"</p>
                <div style={scoreTextStyle}>AI 최종 산정 기여도: <strong style={{color: item.tagColor}}>{item.score}%</strong></div>
              </div>
            ))}
            
            <button onClick={onClose} style={confirmBtnStyle}>리포트 확인 완료</button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// --- 스타일 정의 (원본과 동일하게 유지) ---
const overlayStyle = { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 3000 };
const modalCardStyle = { backgroundColor: '#fff', width: '520px', borderRadius: '35px', padding: '35px', boxShadow: '0 25px 60px rgba(0,0,0,0.4)', position: 'relative', border: '1px solid #E2E8F0' };
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' };
const titleStyle = { margin: 0, fontSize: '20px', fontWeight: '1000', color: '#1A1A1B', letterSpacing: '-0.5px' };
const closeBtnStyle = { border: 'none', background: 'none', fontSize: '30px', cursor: 'pointer', color: '#999', display: 'flex', alignItems: 'center' };

const loadingAreaStyle = { textAlign: 'center', padding: '60px 0' };
const spinnerStyle = { width: '50px', height: '50px', border: '5px solid #f3f3f3', borderTop: '5px solid #4A90E2', borderRadius: '50%', margin: '0 auto', animation: 'spin 1s linear infinite' };
const loadingTextStyle = { fontSize: '17px', fontWeight: '1000', color: '#1A1A1B', marginTop: '25px' };
const subLoadingStyle = { fontSize: '13px', color: '#777', marginTop: '10px', fontWeight: '600' };

const contentAreaStyle = { maxHeight: '500px', overflowY: 'auto', paddingRight: '5px' };
const summaryBoxStyle = { backgroundColor: '#F0F7FF', padding: '18px', borderRadius: '20px', border: '1px solid #CEE4FF', marginBottom: '25px', fontSize: '14px', lineHeight: '1.6', color: '#2C3E50' };
const reportCardStyle = { backgroundColor: '#F8FAFC', borderRadius: '24px', padding: '24px', marginBottom: '20px', border: '1px solid #E2E8F0', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' };
const cardTopStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' };
const nameStyle = { fontSize: '18px', fontWeight: '1000', color: '#111' };
const gaugeBarStyle = { width: '100%', height: '10px', backgroundColor: '#E2E8F0', borderRadius: '5px', overflow: 'hidden', marginBottom: '15px' };
const gaugeProgressStyle = { height: '100%', transition: 'width 1.5s cubic-bezier(0.4, 0, 0.2, 1)' };
const feedbackStyle = { fontSize: '14px', lineHeight: '1.7', color: '#444', fontStyle: 'italic', margin: '0 0 15px 0', fontWeight: '700', paddingLeft: '5px' };
const scoreTextStyle = { fontSize: '13px', color: '#666', textAlign: 'right', fontWeight: '800' };
const confirmBtnStyle = { width: '100%', padding: '18px', backgroundColor: '#1A1A1B', color: '#fff', border: 'none', borderRadius: '20px', fontWeight: '1000', cursor: 'pointer', marginTop: '10px', fontSize: '16px', transition: 'transform 0.2s' };

export default AIAnalyzeModal;
